import {
  PaginationState, createColumnHelper, flexRender,
  getCoreRowModel, getSortedRowModel, SortingState, useReactTable,
} from "@tanstack/react-table";
import DivLoader from "components/divloader/DivLoader";
import React, { useEffect, useState } from "react";
import Pagination from "components/pagination";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import { PayoutRowObj } from "..";
import moment from "moment";
import ShortTruncateCopy from "components/common/ShortTruncateCopy";
import { AiFillMinusCircle, AiFillPlusCircle } from "react-icons/ai";
import Searchbox from "components/fields/Searchbox";
import StatusRender from "views/admin/payments/components/StatusRender";
import InfoModal from "./infoModal";
import CustomToolTip from "components/tooltips/new";
import { getImage } from "utils/commonFunction";
import Card from "components/card";
import AddBatchFile from "./AddBatchFile";

const columnHelper = createColumnHelper<PayoutRowObj>();

function PayoutList(props: {
  tableData: any; fetchPayouts: () => void; onValueChange: (value: string) => void;
  roleData?: any; isLoading: boolean; page: number; setPage: any;
  totalpage: number; totalItems: number; currentPage: number;
  pageSize: number; setPageSize: any; filterData: () => void;
  filter: any; setFilter: (e: any) => void;
  setLocalFilters?: (e: any) => void; setClearClick: () => void; localFilters?: any;
}) {
  const {
    tableData, onValueChange, isLoading, roleData, page, setPage,
    totalpage, totalItems, currentPage, pageSize, setPageSize, fetchPayouts,
    filter, setFilter, setClearClick, setLocalFilters, localFilters, filterData,
  } = props;

  const [{ pageIndex }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize });
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  React.useEffect(() => { setExpandedRow(null); }, [page, pageSize]);
  useEffect(() => {
    setData(tableData?.map((item: any) => ({ ...item })) ?? []);
  }, [tableData]);

  const handleExpand = (rowId: any) => setExpandedRow(expandedRow === rowId ? null : rowId);

  const columns = [
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => <span className="bp-th">Created Date</span>,
      cell: (info: any) => (
        <button className="bp-expand-cell" onClick={() => handleExpand(info?.row?.original?.id)}>
          {expandedRow === info?.row?.original?.id
            ? <AiFillMinusCircle className="bp-expand-icon bp-expand-icon--minus" />
            : <AiFillPlusCircle className="bp-expand-icon bp-expand-icon--plus" />}
          <span className="bp-date">{moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss")}</span>
        </button>
      ),
      size: 200,
    }),
    columnHelper.accessor("batchId", {
      id: "batchId",
      header: () => <span className="bp-th">Batch ID</span>,
      cell: (info) => <ShortTruncateCopy info={info?.getValue()} showCopy={true} />,
    }),
    columnHelper.accessor("clientName", {
      id: "clientName",
      header: () => <span className="bp-th">Client</span>,
      cell: (info) => (
        <div className="bp-client-cell">
          <div className="bp-client-avatar">{info.getValue()?.charAt(0)?.toUpperCase() || "?"}</div>
          <span className="bp-client-name">{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor("totalTransactions", {
      id: "totalTransactions",
      header: () => <span className="bp-th">TX #</span>,
      cell: (info: any) => <span className="bp-num">{info.getValue()}</span>,
    }),
    columnHelper.accessor("gatewayName", {
      id: "gatewayName",
      header: () => <span className="bp-th">Gateway {roleData[0]?.payout?.value?.payout_show_method_name ? "Name" : "Hash ID"}</span>,
      cell: (info: any) => (
        !roleData[0]?.payout?.value?.payout_show_method_name
          ? <ShortTruncateCopy info={info?.row?.original?.gatewayId || "—"} showCopy={true} />
          : <div className="bp-gateway-cell">
              <img style={{ height: "auto", width: "14px" }} src={getImage(info.getValue())} alt="" />
              <span className="bp-gateway-name">{info.getValue() || "—"}</span>
            </div>
      ),
    }),
    columnHelper.accessor("totalAmount", {
      id: "totalAmount",
      header: () => <span className="bp-th">Batch Total</span>,
      cell: (info: any) => <span className="bp-amount">{Number(info.getValue() ?? 0).toFixed(2)}</span>,
    }),
  ];

  const table = useReactTable({
    data, columns,
    state: { pagination, sorting },
    onPaginationChange: setPagination, onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(),
  });

  const handleValueChange = (e: any) => { setExpandedRow(null); onValueChange(e); };

  const findLastErrorMessage = (obj: any): string | null => {
    const errorKeys = ["errorMessage","message","decline_reason","error_message","failure_message","failure_code"];
    let lastValue: string | null = null;
    const search = (data: any) => {
      if (!data || typeof data !== "object") return;
      for (const key of Object.keys(data)) {
        if (errorKeys.includes(key) && key !== "_content") lastValue = data[key];
        if (typeof data[key] === "object") search(data[key]);
        if (key === "_content" && typeof data[key] === "string") {
          try {
            const p = JSON.parse(data[key]);
            if (p.decline_reason) lastValue = p.decline_reason;
            else for (const k of Object.keys(p)) if (errorKeys.includes(k)) lastValue = p[k];
          } catch {}
        }
      }
    };
    search(obj); return lastValue;
  };

  const parsePythonJSON = (str: string): any[] => {
    try {
      return JSON.parse(
        str.replace(/\bNone\b/g,"null").replace(/\bFalse\b/g,"false").replace(/\bTrue\b/g,"true")
          .replace(/([{,]\s*)'([^']+)'\s*:/g,'$1"$2":').replace(/:\s*'([^']*)'/g,': "$1"')
      );
    } catch { return []; }
  };

  return (
    <div className="bp-card">
      {/* Header */}
      <div className="bp-header">
        <div className="bp-header-left">
          <div className="bp-header-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2"/>
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
            </svg>
          </div>
          <div>
            <h3 className="bp-title">Regular Payouts</h3>
            <p className="bp-subtitle">
                Batch payout records
              {/* {totalItems > 0 ? `${totalItems.toLocaleString()} total batch${totalItems !== 1 ? "es" : ""}` : "No batches"} */}
            </p>
          </div>
        </div>
        <div className="bp-header-right">
          {roleData?.[0]?.payout?.value?.add_payout_batch_file && (
            <AddBatchFile fetchPayouts={fetchPayouts} />
          )}
          <Searchbox onSearch={handleValueChange} />
        </div>
      </div>

      {/* Table */}
      <div className="bp-table-wrap">
        {isLoading ? (
          <div className="bp-loading">
            <div className="bp-spinner" />
            <p className="bp-loading-txt">Loading batch payouts…</p>
          </div>
        ) : (
          <table className="bp-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id} colSpan={h.colSpan}
                      onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined}
                      className={`bp-thead-th${h.column.getCanSort() ? " bp-thead-th--sort" : ""}`}
                    >
                      <div className="bp-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="bp-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> :
                             h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> :
                             <RiArrowUpDownFill size={11} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, ri) => (
                  <React.Fragment key={row.id}>
                    <tr className={`bp-tbody-tr${ri % 2 !== 0 ? " bp-tbody-tr--alt" : ""}`}>
                      {row.getVisibleCells().map((cell, ci) => (
                        <td key={ci} className="bp-tbody-td">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>

                    {/* Expanded sub-table */}
                    {expandedRow === (row.original as any).id && (
                      <tr className="bp-expanded-row">
                        <td colSpan={columns.length} className="bp-expanded-cell">
                          <div className="bp-subtable-wrap">
                            <h4 className="bp-subtable-heading">Transactions</h4>
                            {Object.keys((row.original as any).transactions ?? {}).length > 0 ? (
                              <table className="bp-table">
                                <thead>
                                  <tr>
                                    {[
                                      "Created At", "Amount", "Transaction ID",
                                      "Internal ID", "Status",
                                      ...(roleData?.[0]?.payout?.value?.view_payout_response_information ? ["Action"] : []),
                                    ].map((h) => (
                                      <th key={h} className="bp-thead-th">
                                        <span className="bp-th">{h}</span>
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries((row.original as any).transactions).map(([txId, tx]: [string, any]) => (
                                    <tr key={txId} className="bp-tbody-tr">
                                      <td className="bp-tbody-td">
                                        <span className="bp-date">{moment(tx.createdAt).format("YYYY-MM-DD HH:mm:ss")}</span>
                                      </td>
                                      <td className="bp-tbody-td">
                                        <span className="bp-amount">{Number(tx.amount ?? 0).toFixed(2)} {tx.currency}</span>
                                      </td>
                                      <td className="bp-tbody-td">
                                        <ShortTruncateCopy info={tx.transactionId} showCopy={true} />
                                      </td>
                                      <td className="bp-tbody-td">
                                        <ShortTruncateCopy info={tx.internalId} showCopy={true} />
                                      </td>
                                      <td className="bp-tbody-td">
                                        <CustomToolTip
                                          content={(() => {
                                            try {
                                              const hist = Array.isArray(tx.paymentHistory)
                                                ? tx.paymentHistory
                                                : typeof tx.paymentHistory === "string"
                                                ? parsePythonJSON(tx.paymentHistory)
                                                : [];
                                              if (!hist.length) return <span className="bp-no-history">No history available.</span>;
                                              return (hist[0] ?? hist).map((h: any, i: number) => {
                                                const isDeclined = ["DECLINED","ERRORED","FAILED"].includes(h.status);
                                                const err = isDeclined ? (findLastErrorMessage(tx.gatewayResponse) ?? "") : "";
                                                return (
                                                  <Card className="m-2 shadow" key={i}>
                                                    <div className="border border-white p-3 max-w-[60vh]">
                                                      <p>Created at {h?.created_at} by API call from [{h?.client} - {h?.ip}] with status {h?.status}
                                                        {isDeclined && <><br />Reason: {err || "Not mentioned by gateway"}</>}
                                                      </p>
                                                    </div>
                                                  </Card>
                                                );
                                              });
                                            } catch { return <span>Error loading history</span>; }
                                          })()}
                                          trigger={
                                            <button className="bp-status-btn">
                                              <StatusRender status={tx.status} value={tx.status} />
                                            </button>
                                          }
                                          header="Status History"
                                        />
                                      </td>
                                      {roleData?.[0]?.payout?.value?.view_payout_response_information && (
                                        <td className="bp-tbody-td">
                                          <InfoModal info={tx.gatewayResponse} />
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div className="bp-subtable-empty">No transactions available.</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr><td colSpan={10}>
                  <div className="bp-empty">
                    <div className="bp-empty-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="7" width="20" height="14" rx="2"/>
                        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                      </svg>
                    </div>
                    <p className="bp-empty-title">No batch payouts found</p>
                    <p className="bp-empty-sub">Upload a batch file to get started</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="bp-pagination-wrap">
        <Pagination setPage={setPage} page={page} totalpage={totalpage}
          currentPage={currentPage} pageSize={pageSize} setPageSize={setPageSize} arraySize={[50, 100, 200]} />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .bp-card { font-family:'Plus Jakarta Sans',sans-serif; background:#fff;  border-radius:18px; overflow:hidden; }

        /* Header */
        .bp-header { padding:15px 20px 12px; border-bottom:1px solid #f1f5f9; display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; }
        .bp-header-left { display:flex; align-items:center; gap:10px; }
        .bp-header-icon {
            width: 36px;
            height: 36px;
            border-radius: 10px;
            background: linear-gradient(135deg, #eff6ff, #dbeafe);
            border: 1.5px solid #bfdbfe;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #2563eb;
            flex-shrink: 0;
        }
        .bp-title { 
            font-size: 14px;
            font-weight: 700;
            color: #0f172a;
            letter-spacing: -.2px;
            margin-bottom: 1px;
            line-height: 20px;
        }
        .bp-subtitle { 
            font-size: 11.5px;
            color: #94a3b8;
            font-weight: 500;
        }
        .bp-header-right { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

        /* Table */
        .bp-table-wrap { overflow-x:auto; }
        .bp-table-wrap::-webkit-scrollbar { height:4px; }
        .bp-table-wrap::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }
        .bp-table { width:100%; border-collapse:collapse; }

        .bp-thead-th { padding:11px 14px 9px; background:#f8fafc; border-bottom:1.5px solid #e2e8f0; text-align:left; white-space:nowrap; user-select:none; }
        .bp-thead-th--sort { cursor:pointer; }
        .bp-thead-th--sort:hover { background:#f1f5f9; }
        .bp-th-inner { display:flex; align-items:center; gap:5px; }
        .bp-th { font-size:11px; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:0.5px; }
        .bp-sort-icon { color:#94a3b8; display:flex; align-items:center; }

        .bp-tbody-tr { transition:background .12s; }
        .bp-tbody-tr:hover { background:#f8fafc; }
        .bp-tbody-tr--alt { background:#fafbfc; }
        .bp-tbody-td { padding:11px 14px; border-bottom:1px solid #f1f5f9; vertical-align:middle; }

        /* Cells */
        .bp-expand-cell { display:inline-flex; align-items:center; gap:7px; background:none; border:none; cursor:pointer; padding:0; font-family:'Plus Jakarta Sans',sans-serif; }
        .bp-expand-icon { width:18px; height:18px; flex-shrink:0; }
        .bp-expand-icon--plus  { color:#2563eb; }
        .bp-expand-icon--minus { color:#dc2626; }
        .bp-date { font-size:12px; font-weight:600; color:#0f172a; white-space:nowrap; }
        .bp-client-cell { display:flex; align-items:center; gap:7px; }
        .bp-client-avatar { width:26px; height:26px; border-radius:7px; background:linear-gradient(135deg,#eff6ff,#dbeafe); border:1.5px solid #bfdbfe; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:800; color:#2563eb; flex-shrink:0; }
        .bp-client-name { font-size:12.5px; font-weight:600; color:#0f172a; white-space:nowrap; }
        .bp-num { font-size:12.5px; font-weight:700; color:#0f172a; }
        .bp-gateway-cell { display:flex; align-items:center; gap:6px; }
        .bp-gateway-name { font-size:12.5px; font-weight:600; color:#0f172a; }
        .bp-amount { font-size:13px; font-weight:700; color:#0f172a; white-space:nowrap; }
        .bp-status-btn { background:none; border:none; cursor:pointer; display:flex; align-items:center; }
        .bp-no-history { font-size:12px; color:#94a3b8; font-style:italic; }

        /* Expanded row */
        .bp-expanded-cell { padding:0 !important; border-bottom:2px solid #e2e8f0; }
        .bp-subtable-wrap { background:#f0f7ff; border-left:3px solid #bfdbfe; padding:14px 16px; overflow-x:auto; }
        .bp-subtable-heading { font-size:12px; font-weight:700; color:#1e3a8a; text-transform:uppercase; letter-spacing:.5px; margin-bottom:12px; }
        .bp-subtable-empty { font-size:13px; color:#64748b; font-weight:500; text-align:center; padding:20px; }

        /* Loading / empty */
        .bp-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px; gap:12px; }
        .bp-spinner { width:28px; height:28px; border:3px solid #e2e8f0; border-top-color:#2563eb; border-radius:50%; animation:bpSpin .7s linear infinite; }
        @keyframes bpSpin { to{transform:rotate(360deg)} }
        .bp-loading-txt { font-size:13px; color:#94a3b8; font-weight:500; }
        .bp-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px 24px; gap:10px; }
        .bp-empty-icon { width:52px; height:52px; border-radius:13px; background:#f8fafc; border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#cbd5e1; }
        .bp-empty-title { font-size:14px; font-weight:700; color:#64748b; }
        .bp-empty-sub { font-size:12.5px; color:#94a3b8; font-weight:500; text-align:center; }

        .bp-pagination-wrap { padding:12px 16px; border-top:1px solid #f1f5f9; background:#fafbfc; }

        @media (max-width:700px) { .bp-header { flex-direction:column; align-items:flex-start; } .bp-header-right { width:100%; } }
      `}</style>
    </div>
  );
}

export default PayoutList;